import { useCallback, useEffect, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { startOfDay } from 'date-fns';
import { useAtomValue } from 'jotai';

import {
  recentNotesViewAtom,
  searchAtom,
  selectDateAtom,
} from '@/modules/patientList/atoms';
import { endpoints } from '@/react-query/endpoints';
import { useBatchNotesByStandalonePatientVisitIds } from '@/react-query/videa-notes/useBatchNotesByStandalonePatientVisitIds';
import { useBatchNotesByUnifiedAppointmentIds } from '@/react-query/videa-notes/useBatchNotesByUnifiedAppointmentIds';
import type {
  VoiceNotesSearchStandaloneVisit,
  VoiceNotesSearchUnifiedAppointment,
} from '@/react-query/videa-notes/useCombinedAppointments';
import { useCombinedAppointments } from '@/react-query/videa-notes/useCombinedAppointments';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { useRecentNotes } from '@/react-query/videa-notes/useRecentNotes';
import { useDebounce } from '@/utils/hooks/useDebounce';

/**
 * Fetches and combines ambient data for appointments including:
 * - Appointments for a selected date range with optional patient name search
 * - OR recent notes sorted by creation date (when recentNotesView is true)
 * - Clinical notes for each appointment via batch queries
 *
 * Returns appointments enriched with their associated notes,
 * along with loading states and pagination controls.
 */
export const useAmbientAppointmentsData = () => {
  // Atoms
  const search = useAtomValue(searchAtom);
  const selectDate = useAtomValue(selectDateAtom);
  const isRecentNotesView = useAtomValue(recentNotesViewAtom);

  // Debounced search
  const { debouncedValue: debouncedSearch } = useDebounce(search);

  // Date range - simple approach that works with existing API
  const dateRange = useMemo(() => {
    const selectedDate = selectDate || new Date();
    const isSearchValid = debouncedSearch.length > 2;

    if (isSearchValid) {
      return {
        startDate: undefined,
        endDate: undefined,
      };
    }

    // Use selected date as start, add 1 day as end to limit to just that day
    const startDate = startOfDay(new Date(selectedDate));
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);

    return {
      startDate: startDate,
      endDate: endDate,
    };
  }, [selectDate, debouncedSearch]);

  const isUUIDv7 = (uuid: string): boolean => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const isStandalonePatientVisit = useCallback(
    (
      appointment:
        | VoiceNotesSearchStandaloneVisit
        | VoiceNotesSearchUnifiedAppointment,
    ): appointment is VoiceNotesSearchStandaloneVisit => {
      return (
        'standalonePatientVisitId' in appointment &&
        isUUIDv7(appointment.standalonePatientVisitId)
      );
    },
    [],
  );

  const isUnifiedAppointment = useCallback(
    (
      appointment:
        | VoiceNotesSearchUnifiedAppointment
        | VoiceNotesSearchStandaloneVisit,
    ): appointment is VoiceNotesSearchUnifiedAppointment => {
      return !isStandalonePatientVisit(appointment);
    },
    [isStandalonePatientVisit],
  );

  const queryClient = useQueryClient();
  // Callback method to invalidate the notes cache
  const invalidateNotesCache = () => {
    // Call invalidateQueries separately for each endpoint to properly match query keys
    queryClient.invalidateQueries({
      queryKey: [endpoints.ambient.voiceNotes.combinedAppointmentsList],
    });
    queryClient.invalidateQueries({
      queryKey: [endpoints.ambient.voiceNotes.searchNotesByCreatedAt],
    });
    queryClient.invalidateQueries({
      queryKey: [
        endpoints.ambient.voiceNotes.batchNotesByStandalonePatientVisitIds,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
      ],
    });
  };

  // Fetch combined appointments OR recent notes based on view mode
  // When in recent notes mode, ignore date filters but support patient name search
  const combinedAppointmentsQuery = useCombinedAppointments(
    {
      appointmentOpts: {
        startDate: dateRange.startDate?.toISOString(),
        endDate: dateRange.endDate?.toISOString(),
      },
      patientOpts: {
        name: debouncedSearch || undefined,
      },
      meta: {
        sort: {
          appointmentOn: 'ASC',
        },
        pagination: {
          limit: 10,
        },
      },
    },
    { enabled: !isRecentNotesView },
  );

  const recentNotesQuery = useRecentNotes(
    {
      patientOpts: {
        name: debouncedSearch || undefined,
      },
      meta: {
        pagination: {
          limit: 10,
        },
      },
    },
    { enabled: isRecentNotesView },
  );

  // Use the appropriate query based on the view mode
  const {
    data: combinedAppointmentsData,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = isRecentNotesView ? recentNotesQuery : combinedAppointmentsQuery;

  const hasPages = !!combinedAppointmentsData?.pages?.length;

  // Process appointments data
  const appointments = useMemo(() => {
    if (!combinedAppointmentsData?.pages) return [];

    const allAppointments = combinedAppointmentsData.pages.flatMap(
      (page) => page.appointments || [],
    );

    return allAppointments;
  }, [combinedAppointmentsData]);

  // Extract unified appointment IDs for notes batch request
  const unifiedAppointmentIds = useMemo(() => {
    return appointments
      .filter(isUnifiedAppointment)
      .map((appointment) => appointment.unifiedAppointmentId);
  }, [appointments, isUnifiedAppointment]);

  // Extract standalone patient visit IDs for notes batch request
  const standalonePatientVisitIds = useMemo(() => {
    return appointments
      .filter(isStandalonePatientVisit)
      .map((appointment) => appointment.standalonePatientVisitId);
  }, [appointments, isStandalonePatientVisit]);

  // Fetch notes data
  const enableUnifiedVoiceNotesQuery = !!unifiedAppointmentIds.length;
  const voiceNotesForUnifiedAppointments = useBatchNotesByUnifiedAppointmentIds(
    unifiedAppointmentIds,
    enableUnifiedVoiceNotesQuery,
  );

  const enableStandaloneVoiceNotesQuery = !!standalonePatientVisitIds.length;
  const voiceNotesForStandalonePatientVisits =
    useBatchNotesByStandalonePatientVisitIds(
      standalonePatientVisitIds,
      enableStandaloneVoiceNotesQuery,
    );

  // Process notes data - combine all batch results into a single map
  const processedVoiceNotesForUnifiedAppointments = useMemo(() => {
    return (
      voiceNotesForUnifiedAppointments?.reduce(
        (allNotes, queryResult) => {
          // Extract appointments from this batch result
          const batchVoiceNotesForUnifiedAppointments =
            queryResult.data?.appointments;

          // If batch has appointments, merge them into our accumulated map
          // If no appointments, return the accumulated map unchanged
          return batchVoiceNotesForUnifiedAppointments
            ? { ...allNotes, ...batchVoiceNotesForUnifiedAppointments }
            : allNotes;
        },
        {} as Record<string, ClinicalNote[]>,
      ) || {}
    );
  }, [voiceNotesForUnifiedAppointments]);

  // Process standalone patient visit notes data
  const processedVoiceNotesForStandalonePatientVisits = useMemo(() => {
    return (
      voiceNotesForStandalonePatientVisits?.reduce(
        (allNotes, queryResult) => {
          // Extract patient visits from this batch result (standalone uses 'patientVisits' key)
          const batchVoiceNotesForStandalonePatientVisits =
            queryResult.data?.patientVisits;

          // If batch has patient visits, merge them into our accumulated map
          // If no patient visits, return the accumulated map unchanged
          return batchVoiceNotesForStandalonePatientVisits
            ? { ...allNotes, ...batchVoiceNotesForStandalonePatientVisits }
            : allNotes;
        },
        {} as Record<string, ClinicalNote[]>,
      ) || {}
    );
  }, [voiceNotesForStandalonePatientVisits]);

  const appointmentsWithNotes = useMemo(() => {
    return appointments.map((appointment) => {
      const isStandalone = isStandalonePatientVisit(appointment);

      // Get the appropriate ID based on appointment type
      const appointmentId = isStandalone
        ? appointment.standalonePatientVisitId
        : appointment.unifiedAppointmentId;

      // Get notes for this appointment
      const notes = isStandalone
        ? processedVoiceNotesForStandalonePatientVisits[appointmentId] || []
        : processedVoiceNotesForUnifiedAppointments[appointmentId] || [];

      const appointmentOnDateTime = new Date(appointment.appointmentOn);

      // Get patient info
      const patient = isStandalone
        ? {
          firstName: appointment.firstName,
          lastName: appointment.lastName,
          dob: appointment.dateOfBirth ?? undefined,
          sourcePatientId: appointment.sourcePatientId,
        }
        : appointment.pmsPatients.length > 0
          ? {
            firstName: appointment.pmsPatients[0].firstName,
            lastName: appointment.pmsPatients[0].lastName,
            dob: appointment.pmsPatients[0].dateOfBirth,
            pmsPatientId: appointment.pmsPatients[0].id,
          }
          : appointment.imagingPatients.length > 0
            ? {
              firstName:
                appointment.imagingPatients[0].name?.split(' ')[0] ?? '',
              lastName:
                appointment.imagingPatients[0].name?.split(' ')[1] ?? '',
              dob: appointment.imagingPatients[0].dateOfBirth,
            }
            : { firstName: '', lastName: '', dob: undefined };

      return {
        // Transform standalone visit to match appointment structure
        appointment: {
          unifiedId: !isStandalone ? appointmentId : '', // Empty for standalone visits - use standalonePatientVisitId instead
          standalonePatientVisitId: isStandalone ? appointmentId : '', // Use the standalone visit ID
          start: appointmentOnDateTime.toISOString(),
          appointmentOn: appointmentOnDateTime.toISOString(),
          noteId: appointment.noteId || null,
          noteCreatedAt: appointment.noteCreatedAt || null,
          provider: isStandalone ? undefined : appointment.provider,
          operatory: isStandalone ? undefined : appointment.operatory,
        },
        lastTreatments: isStandalone
          ? {
            lastBw: null,
            lastFmx: null,
            lastPerioChart: null,
            lastPerioMaintenance: null,
            lastProphy: null,
            lastSrp: null,
          }
          : {
            lastBw: null,
            lastFmx: null,
            lastPerioChart: null,
            lastPerioMaintenance: null,
            lastProphy: null,
            lastSrp: null,
          },
        patient,
        notes,
        isStandalone,
      };
    });
  }, [
    appointments,
    isStandalonePatientVisit,
    processedVoiceNotesForStandalonePatientVisits,
    processedVoiceNotesForUnifiedAppointments,
  ]);

  // Detect if any writeback is in progress (unified or standalone)
  const isWritebackInProgress = useMemo(() => {
    const isUnifiedInProgress =
      voiceNotesForUnifiedAppointments?.some((batchResult) => {
        if (!batchResult.data?.appointments) return false;
        const allNotes = Object.values(batchResult.data.appointments).flat();
        return allNotes.some(
          (note) =>
            note.writebackStatus?.clinicalNoteId &&
            !note.writebackStatus?.processedAt &&
            note.writebackStatus?.errorCode === null,
        );
      }) ?? false;

    const isStandaloneInProgress =
      voiceNotesForStandalonePatientVisits?.some((batchResult) => {
        if (!batchResult.data?.patientVisits) return false;
        const allNotes = Object.values(batchResult.data.patientVisits).flat();
        return allNotes.some(
          (note) =>
            note.writebackStatus?.clinicalNoteId &&
            !note.writebackStatus?.processedAt &&
            note.writebackStatus?.errorCode === null,
        );
      }) ?? false;

    return isUnifiedInProgress || isStandaloneInProgress;
  }, [voiceNotesForUnifiedAppointments, voiceNotesForStandalonePatientVisits]);

  // Poll for updates when writeback is in progress (unified or standalone)
  useEffect(() => {
    if (!isWritebackInProgress) return;
    const refetchAllBatches = () => {
      voiceNotesForUnifiedAppointments?.forEach((batchResult) => {
        batchResult.refetch();
      });
      voiceNotesForStandalonePatientVisits?.forEach((batchResult) => {
        batchResult.refetch();
      });
    };
    const interval = setInterval(refetchAllBatches, 5 * 1000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [
    isWritebackInProgress,
    voiceNotesForUnifiedAppointments,
    voiceNotesForStandalonePatientVisits,
  ]);

  const isUnifiedNotesLoading = enableUnifiedVoiceNotesQuery && voiceNotesForUnifiedAppointments.some(
    (q) => q.isLoading,
  );
  const isStandaloneNotesLoading = enableStandaloneVoiceNotesQuery && voiceNotesForStandalonePatientVisits.some(
    (q) => q.isLoading,
  );

  return {
    appointmentsWithNotes,
    isLoading: isLoading || isUnifiedNotesLoading || isStandaloneNotesLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    hasPages,
    invalidateNotesCache,
    isUnifiedNotesLoading,
    isStandaloneNotesLoading,
  };
};
